import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import type { VocabularyEntry } from "@/lib/phaseVocabulary";
import { INPUT_CLS } from "@/components/strategy/FieldEditors";

/**
 * Multi-line text that may contain `{{path}}` placeholders, with a picker
 * that inserts one at the caret so an operator never has to remember the
 * namespace. Commits on blur; an empty text commits as null (remove).
 */
export default function TemplateTextField({
  value,
  onCommit,
  variables,
  ariaLabel,
  placeholder,
  rows = 8,
  disabled,
}: {
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  variables: VocabularyEntry[];
  ariaLabel: string;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}) {
  const saved = value ?? "";
  const [draft, setDraft] = useState(saved);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(saved), [saved]);

  const commit = (text: string) => {
    const next = text.trim() === "" ? null : text;
    if ((next ?? "") !== saved) onCommit(next);
  };

  const insert = (path: string) => {
    const el = ref.current;
    const token = `{{${path}}}`;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + token + draft.slice(end);
    setDraft(next);
    // The inserted token is an edit like any other: commit it now so the
    // picker works even when the operator never focuses the textarea.
    commit(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const groups = new Map<string, VocabularyEntry[]>();
  for (const v of variables) {
    const list = groups.get(v.group) ?? [];
    list.push(v);
    groups.set(v.group, list);
  }

  return (
    <div>
      <textarea
        ref={ref}
        aria-label={ariaLabel}
        className={cn(INPUT_CLS, "font-mono text-xs")}
        rows={rows}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
      />
      {!disabled ? (
        <select
          aria-label={`Insert variable into ${ariaLabel.toLowerCase()}`}
          className="mt-1 max-w-[16rem] rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-xs"
          value=""
          onChange={(e) => {
            if (e.target.value) insert(e.target.value);
          }}
        >
          <option value="">Insert variable…</option>
          {[...groups.entries()].map(([group, entries]) => (
            <optgroup key={group} label={group}>
              {entries.map((v) => (
                <option key={v.path} value={v.path}>
                  {v.label} — {`{{${v.path}}}`}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : null}
    </div>
  );
}
